import { isDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  useCheckTargetAccumulatorByTradeCapture,
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TargetAccumulatorFormField } from '@/App/tiles/TargetAccumulator/TargetAccumulatorFormField.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTargetAccumulatorValidation } from '@/store/targetAccumulator/selectors.ts';

const Target = () => {
  const { formatMessage } = useIntl();
  const tileId = useTargetAccumulatorTileId();
  const target = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.target);
  const validation = useAppSelector((state) => selectTargetAccumulatorValidation(state, tileId));
  const checkTargetAccumulatorWithTradeCapture = useCheckTargetAccumulatorByTradeCapture();
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const handleTarget = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Target changed', isDefined(value) ? String(value) : 'cleared');

    return checkTargetAccumulatorWithTradeCapture({
      target: isDefined(value) ? value : null,
    });
  };

  return (
    <TargetAccumulatorFormField
      formattedMessageId="targetAccumulator.form.target"
      extra={
        <Tooltip position="right" contents={<FormattedMessage id="targetAccumulator.form.target.info" />}>
          <div className="badge badge-dismissible">
            <em className="icon icon-sm cursor-default line-height-1 text-secondary">info_outline</em>
          </div>
        </Tooltip>
      }
    >
      <TradeCaptureValidationWrapper validation={validation?.target}>
        <SgNumberInput
          valuePosition="start"
          unitPosition="end"
          selectAllOnFocus
          value={target}
          maxDecimals={9}
          onChange={handleTarget}
          size="sm"
          unit={formatMessage({ id: 'targetAccumulator.form.target.rateGain' })}
          data-e2e="target"
        />
      </TradeCaptureValidationWrapper>
    </TargetAccumulatorFormField>
  );
};

export default Target;
